/**
 * Table-driven authorization decision for the backend domain layer.
 *
 * This module lives in the backend domain layer under `src/server/`, kept out of
 * the React `src/components` tree. It is the single, server-side source of truth
 * for whether a subject may perform a protected {@link Action} on a
 * {@link Resource}. Every protected domain operation consults {@link can}, and
 * the React layer derives its action-gating from the same decision so the UI
 * never diverges from the enforced policy.
 *
 * The decision deliberately combines two separate concepts that the domain keeps
 * apart (see `./types/authorization`):
 *
 * - the subject's tenant-wide {@link AccountRole} — *who they are*, and
 * - the {@link ProgrammeResponsibility} of the assignment whose `programmeId`
 *   matches a programme-scoped resource — *what they may do on that programme*.
 *
 * @module server/authorization
 */

import type {
  AccountRole,
  ModuleOwnershipAssignment,
  ProgrammeResponsibility,
  ProgrammeResponsibilityAssignment,
  TenantCapability,
} from "./types";

/** Capabilities that may be persisted on a tenant membership. */
export const TENANT_CAPABILITIES: readonly TenantCapability[] = ["programme:create"];

/**
 * A protected action the authorization engine can decide.
 *
 * Programme-scoped actions (`programme:*`, `workflow:transition`) apply to a
 * `{ type: "programme" }` {@link Resource}; tenant-admin actions
 * (`assignment:manage`, `settings:manage`, `support-access:manage`) apply to the
 * tenant.
 */
export type Action =
  | "programme:create"
  | "programme:read"
  | "programme:write"
  | "programme:approve"
  | "programme:review"
  | "workflow:transition"
  | "delivery-status:manage"
  | "assignment:manage"
  | "settings:manage"
  | "support-access:manage"
  | "audit:read";

/**
 * The resource an {@link Action} is evaluated against.
 *
 * A discriminated union: tenant-admin actions target the tenant, while
 * programme-scoped actions target a specific programme identified by
 * `programmeId`, which selects the matching {@link ProgrammeResponsibility}.
 */
export type Resource = { type: "tenant" } | { type: "programme"; programmeId: string };

/**
 * The subject an authorization decision is made for.
 *
 * Attaches both the tenant-wide {@link AccountRole} and the subject's
 * per-programme {@link ProgrammeResponsibilityAssignment}s to the
 * session/tenant/programme context, so {@link can} can reason about who the
 * person is and what they may do on a given programme independently.
 */
export interface AuthorizationSubject {
  /** Stable identifier of the subject (user). */
  userId: string;
  /** The subject's tenant-wide account role. */
  accountRole: AccountRole;
  /** Tenant-wide delegated capabilities resolved from the active membership. */
  tenantCapabilities?: readonly TenantCapability[];
  /** The subject's per-programme responsibility assignments. */
  responsibilities: readonly ProgrammeResponsibilityAssignment[];
}

/**
 * Whether the subject holds one of the `allowed` responsibilities for a
 * specific programme.
 *
 * The decision is read only from the subject's own assignments whose
 * `programmeId` matches the resource, so a responsibility on one programme never
 * grants access to another.
 */
function hasProgrammeResponsibility(
  subject: AuthorizationSubject,
  programmeId: string,
  allowed: readonly ProgrammeResponsibility[],
): boolean {
  return subject.responsibilities.some(
    (assignment) =>
      assignment.programmeId === programmeId && allowed.includes(assignment.responsibility),
  );
}

/**
 * Whether a subject acts for the organisation rather than for one programme.
 *
 * An administrator creating or restoring a programme names its lead; anybody
 * else becomes the lead of what they create.
 *
 * @param subject - The caller.
 * @returns Whether they hold a client or platform administrator role.
 */
export function isAdministrativeAccount(subject: AuthorizationSubject): boolean {
  return subject.accountRole === "client-admin" || subject.accountRole === "platform-admin";
}

/**
 * Decide whether a subject may perform a protected action on a resource.
 *
 * The decision combines the subject's account role with the programme
 * responsibility whose `programmeId` matches a programme-scoped resource:
 *
 * - a `deactivated` account role is denied every action on every resource;
 * - `programme:read` is permitted to any active subject on a programme;
 * - programme-scoped writes (`programme:write`) require a `builder` or `lead`
 *   responsibility for that programme;
 * - `programme:approve` requires the tenant-level `client-admin` role;
 * - `programme:review` requires `client-admin` or a `reviewer`, `lead`, or
 *   `approver` responsibility for that programme;
 * - `workflow:transition` requires any non-viewer responsibility for that
 *   programme;
 * - `assignment:manage` on the tenant requires `client-admin` (or
 *   `platform-admin`); on a programme it is additionally permitted to a caller
 *   holding a `lead` responsibility for that programme;
 * - `settings:manage` (tenant/provider-settings management) requires
 *   `client-admin` (or `platform-admin`) on the tenant;
 * - `audit:read` requires `client-admin` (or `platform-admin`) on the tenant,
 *   or a `lead` responsibility for the programme on a programme-scoped resource;
 * - support-access management (`support-access:manage`) requires
 *   `platform-admin`.
 *
 * @param subject - The subject the decision is made for.
 * @param action - The protected action being attempted.
 * @param resource - The resource the action targets.
 * @returns `true` when the action is permitted, otherwise `false`.
 */
export function can(subject: AuthorizationSubject, action: Action, resource: Resource): boolean {
  if (subject.accountRole === "deactivated") {
    return false;
  }

  switch (action) {
    case "programme:create":
      return (
        resource.type === "tenant" &&
        (subject.accountRole === "client-admin" ||
          subject.accountRole === "platform-admin" ||
          (subject.tenantCapabilities ?? []).includes("programme:create"))
      );
    case "delivery-status:manage":
      return resource.type === "programme" && subject.accountRole === "client-admin";
    case "support-access:manage":
      return subject.accountRole === "platform-admin";
    case "settings:manage":
      return (
        resource.type === "tenant" &&
        (subject.accountRole === "client-admin" || subject.accountRole === "platform-admin")
      );
    case "audit:read":
      if (resource.type === "tenant") {
        return subject.accountRole === "client-admin" || subject.accountRole === "platform-admin";
      }
      return hasProgrammeResponsibility(subject, resource.programmeId, ["lead"]);
    case "assignment:manage": {
      const isTenantAdmin =
        subject.accountRole === "client-admin" || subject.accountRole === "platform-admin";
      if (resource.type === "tenant") {
        return isTenantAdmin;
      }
      return isTenantAdmin || hasProgrammeResponsibility(subject, resource.programmeId, ["lead"]);
    }
    case "programme:read":
      return resource.type === "programme";
    case "programme:write":
      return (
        resource.type === "programme" &&
        hasProgrammeResponsibility(subject, resource.programmeId, ["builder", "lead"])
      );
    case "programme:approve":
      return resource.type === "programme" && subject.accountRole === "client-admin";
    case "programme:review":
      return (
        resource.type === "programme" &&
        (subject.accountRole === "client-admin" ||
          hasProgrammeResponsibility(subject, resource.programmeId, [
            "reviewer",
            "lead",
            "approver",
          ]))
      );
    case "workflow:transition":
      return (
        resource.type === "programme" &&
        hasProgrammeResponsibility(subject, resource.programmeId, [
          "lead",
          "builder",
          "reviewer",
          "approver",
        ])
      );
    default: {
      // Unreachable: every member of the union has a case above, and this
      // assignment is what makes tsc say so if one is ever added without one.
      const unhandled: never = action;
      throw new Error(`Unhandled action: ${String(unhandled)}`);
    }
  }
}

/**
 * Decide whether a caller may create or revoke a specific programme
 * responsibility. Tenant administrators manage every responsibility, while
 * programme leads may delegate only module-building and read-only access.
 */
export function canManageProgrammeResponsibility(
  subject: AuthorizationSubject,
  programmeId: string,
  responsibility: ProgrammeResponsibility,
): boolean {
  if (!can(subject, "assignment:manage", { type: "programme", programmeId })) {
    return false;
  }
  if (subject.accountRole === "client-admin" || subject.accountRole === "platform-admin") {
    return true;
  }
  return ["builder", "viewer", "reader"].includes(responsibility);
}

/**
 * Decide whether a caller may edit a specific module within a programme.
 *
 * Module-scoped edit access combines the programme-wide write decision with
 * module ownership: the caller must be permitted to `programme:write` the
 * programme (via {@link can}) *and* either hold a `lead` responsibility for that
 * programme, since leads edit every module, or be the assigned owner of the module
 * in `ownerships`. The decision is read only from the caller's own
 * responsibilities and the supplied ownership records, never from any UI state.
 *
 * @param caller - The caller whose module-edit authorization is decided.
 * @param programmeId - The programme the module belongs to.
 * @param moduleId - The module/component being edited.
 * @param ownerships - The module-ownership assignments to resolve ownership from.
 * @returns `true` when the caller may edit the module, otherwise `false`.
 */
export function canEditModule(
  caller: AuthorizationSubject,
  programmeId: string,
  moduleId: string,
  ownerships: readonly ModuleOwnershipAssignment[],
): boolean {
  if (!can(caller, "programme:write", { type: "programme", programmeId })) {
    return false;
  }

  const isLead = caller.responsibilities.some(
    (assignment) => assignment.programmeId === programmeId && assignment.responsibility === "lead",
  );
  if (isLead) {
    return true;
  }

  return ownerships.some(
    (ownership) =>
      ownership.programmeId === programmeId &&
      ownership.moduleId === moduleId &&
      ownership.userId === caller.userId,
  );
}

/**
 * Render a human-readable identifier for a {@link Resource}, used in
 * authorization error messages so a denial names the resource it protects.
 */
function describeResource(resource: Resource): string {
  return resource.type === "programme" ? `programme ${resource.programmeId}` : "tenant";
}

/**
 * A named error raised by {@link authorize} when a subject is denied a
 * protected action.
 *
 * It carries the denied {@link Action} and {@link Resource} so callers (and the
 * audit trail) surface an explicit authorization failure rather than proceeding.
 * Its `name` is `"AuthorizationError"` and it is recognisable via `instanceof`,
 * distinct from a generic {@link Error}.
 */
export class AuthorizationError extends Error {
  /** The protected action that was denied. */
  readonly action: Action;

  /** The resource the denied action targeted. */
  readonly resource: Resource;

  constructor(action: Action, resource: Resource) {
    super(`Not authorized to perform "${action}" on ${describeResource(resource)}`);
    this.name = "AuthorizationError";
    this.action = action;
    this.resource = resource;
    Object.setPrototypeOf(this, AuthorizationError.prototype);
  }
}

/**
 * Enforcement guard over {@link can}: pass the subject through when the action
 * is permitted, otherwise raise an {@link AuthorizationError}.
 *
 * Every protected domain operation calls `authorize` so a denied call surfaces
 * an explicit, named authorization error carrying the denied action and
 * resource rather than silently proceeding. When {@link can} permits the action,
 * `authorize` returns the authorized subject without error.
 *
 * @param subject - The subject the decision is made for.
 * @param action - The protected action being attempted.
 * @param resource - The resource the action targets.
 * @returns The authorized `subject` when {@link can} permits the action.
 * @throws {AuthorizationError} When {@link can} denies the action.
 */
export function authorize(
  subject: AuthorizationSubject,
  action: Action,
  resource: Resource,
): AuthorizationSubject {
  if (!can(subject, action, resource)) {
    throw new AuthorizationError(action, resource);
  }
  return subject;
}
